<template>
    <div class="map-natal-w">
        <div class="map-natal">
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
					 :viewBox="`0 0 ${viewBoxWidth} ${viewBoxWidth}`"
					 xml:space="preserve">
                <path v-for="houseLineData in houseLinesCoordsList"
                      class="house-cusp-line" :class="{'bold' : houseLineData.bold}"
                      :d="houseLineData.lineData" shape-rendering="geometricPrecision" />

                <path class="gate-sector" stroke="white" stroke-width="2" shape-rendering="geometricPrecision"
                      v-for="gateSector in gateSectorsData"
                      :class="{'has-planets': gateSector.hasPlanets}"
                      :d="gateSector.svgData" />

                <g v-for="gateNumberCoords in gateNumberCoordsList"
                   :transform="`translate(${gateNumberCoords.x}, ${gateNumberCoords.y})`">
                    <text class="gate-text"
                          x="50%" y="50%"
                          :class="{'has-planets': gateNumberCoords.hasPlanets}"
                          alignment-baseline="middle" dominant-baseline="middle" text-anchor="middle"
                    >{{ gateNumberCoords.gate }}</text>
                </g>

                <g v-for="houseNumberCoords in houseNumberCoordsList"
                     :transform="`translate(${houseNumberCoords.x}, ${houseNumberCoords.y})`">
                  <text class="house-text"
                        x="50%" y="50%"
                        alignment-baseline="middle" dominant-baseline="middle" text-anchor="middle"
                        @mouseover="onHouseMouseover($event, houseNumberCoords.houseCusp)"
                        @mousemove="onDataTooltipMousemove"
                        @mouseleave="onDataTooltipMouseleave"
                  >{{ houseNumberCoords.houseCusp }}</text>
                </g>

                <circle class="zodiac-sector" fill="transparent" shape-rendering="geometricPrecision"
                        v-for="(zodiac, index) in zodiacs"
                        :data-zodiac="zodiac"
                        :cx="`${circleCenter}px`" :cy="`${circleCenter}px`" :r="`${zodiacRadius}px`"
                        :stroke-width="`${zodiacBgThickness}px`"
                        :stroke-dasharray="zodiacSectorDasharray" :stroke-dashoffset="zodiacSectorDashoffset[index]"
                />

                <ZodiacIcon v-for="zodiacCoords in zodiacCoordsList"
                            :zodiac="zodiacCoords.name"
                            :x="zodiacCoords.x" :y="zodiacCoords.y"
                            :width="zodiacSignWidth" :height="zodiacSignWidth" />

                <g v-for="aspectData in aspectsData"
                   v-if="aspectData.type != 'CONJUNCTION'"
                   :class="`aspect-container-${aspectData.mode}`">
                  <path class="aspect-line" :d="aspectData.lineData" shape-rendering="geometricPrecision" />
                  <AspectIcon :aspect="aspectData.type" v-if="aspectData.type != 'OPPOSITION'"
                              :x="aspectData.typeCoords.x" :y="aspectData.typeCoords.y"
                              :width="aspectSignWidth" :height="aspectSignWidth" />
                </g>

                <g v-for="planetCoords in planetCoordsList" :class="`planet-container-${planetCoords.mode}`">
                  <PlanetIcon :planet="planetCoords.name" :mode="planetCoords.mode"
                              :retrograde="planetCoords.retrograde"
                              :x="planetCoords.x" :y="planetCoords.y"
                              :width="planetCoords.signWidth"
                              :height="planetCoords.signWidth"
                              @mouseover="onPlanetMouseover"
                              @mousemove="onDataTooltipMousemove"
                              @mouseleave="onDataTooltipMouseleave" />
                </g>
            </svg>
        </div>

        <div id="tooltip" class="tooltip-map"
             ref="dataTooltip"
             :style="{'left': `${dataTooltipCss.left}px`, 'top': `${dataTooltipCss.top}px`}"
             v-show="!!hoveredPlanetData || !!hoveredHouseCuspData">
            <template v-if="!!hoveredPlanetData">
                <span>{{ hoveredPlanetData.planetName }}</span>
                <i>/</i>
                <span>{{ hoveredPlanetData.longitude }}</span>
                <i>/</i>
                <span>{{ hoveredPlanetData.gate }}</span>
                <i>/</i>
                <svg focusable='false' role='presentation' xmlns='http://www.w3.org/2000/svg' fill='#fff'
                     width='16px' height='16px' viewBox='0 0 32 32'>
                    <ZodiacIcon :zodiac="hoveredPlanetData.zodiac"/>
                </svg>
                <i>/</i>
                <span>{{ hoveredPlanetData.houseCusp }}</span>
            </template>
            <template v-else-if="!!hoveredHouseCuspData">
                <span>{{ hoveredHouseCuspData.longitude }}</span>
                <i>/</i>
                <span>{{ hoveredHouseCuspData.gate }}</span>
                <i>/</i>
                <svg focusable='false' role='presentation' xmlns='http://www.w3.org/2000/svg' fill='#fff'
                     width='16px' height='16px' viewBox='0 0 32 32'>
                    <ZodiacIcon :zodiac="hoveredHouseCuspData.zodiac"/>
                </svg>
            </template>
        </div>
    </div>
</template>

<script>
    import constants from '@/constants'
    import math from '@/math';
    import utils from '@/utils';
    import ZodiacIcon from '@/components/icon/ZodiacIcon';
    import PlanetIcon from '@/components/icon/PlanetIcon';
    import AspectIcon from '@/components/icon/AspectIcon';

    export default {
        name: 'AstroCircle',

        props: ['drawMode', 'swephData'],

        components: {
            ZodiacIcon, PlanetIcon, AspectIcon
        },

        data() {
            return {
                // Ширина контейнера для номера Дома
                houseWidth: 40,
                // Отступ от края поля отрисовки до астрокруга
                circlePadding: 70,

                gateRadius: 500,
                gateNumberRectWidth: 40,
                gateNumberRectHeight: 20,

                zodiacRadius: 440,
                zodiacBgThickness: 30,
                zodiacSignWidth: 22,

                planetSignWidth: {
                    regular: 24,
                    retrograde: 32
                },

                aspectSignWidth: 18,

                hoveredPlanet: {
                    mode: constants.mode.natal,
                    planet: null
                },
                hoveredHouseCusp: null,
                dataTooltipCss: {
                    left: 0,
                    top: 0
                }
            }
        },

        computed: {
            gateLongitudes() {
                return constants.gateLongitudes;
            },

            zodiacs() {
                return constants.zodiacs;
            },

            planets() {
                return constants.planets;
            },

            circleCenter() {
                return this.gateRadius + this.circlePadding;
            },

            viewBoxWidth() {
                return 2 * this.circleCenter;
            },

            natalPositions() {
                return this.swephData.natalPositions || [];
            },

            designPositions() {
                return this.swephData.designPositions || [];
            },

            transitPositions() {
                return this.swephData.transitPositions || [];
            },

            directionPositions() {
                return this.swephData.directionPositions || [];
            },

            progressionPositions() {
                return this.swephData.progressionPositions || [];
            },

            natalAspects() {
                return this.swephData.natalAspects || [];
            },

            transitAspects() {
                return this.swephData.transitAspects || [];
            },

            directionAspects() {
                return this.swephData.directionAspects || [];
            },

            progressionAspects() {
                return this.swephData.progressionAspects || [];
            },

            natalTransitAspects() {
                return this.swephData.natalTransitAspects || [];
            },

            natalDirectionAspects() {
                return this.swephData.natalDirectionAspects || [];
            },

            natalProgressionAspects() {
                return this.swephData.natalProgressionAspects || [];
            },

            houseCusps() {
                return this.swephData.houseCusps || [];
            },

            natalGates() {
                return this.swephData.natalGates || [];
            },

            designGates() {
                return this.swephData.designGates || [];
            },

            transitGates() {
                return this.swephData.transitGates || [];
            },

            directionGates() {
                return this.swephData.directionGates || [];
            },

            progressionGates() {
                return this.swephData.progressionGates || [];
            },

            circleRotate() {
                return this.swephData.house1LongitudeOffset || 0.0;
            },

            additionalMapDrawMode() {
                let additionalMapMode = this.$store.getters.additionalMapMode;
                let isMultimap = additionalMapMode === constants.mode.multimap;

                let additionalMapDrawMode;
                if ((additionalMapMode === constants.mode.transit || isMultimap)
                        && !!this.drawMode.transit)
                    additionalMapDrawMode = constants.mode.transit;
                else if ((additionalMapMode === constants.mode.direction || isMultimap)
                        && !!this.drawMode.direction)
                    additionalMapDrawMode = constants.mode.direction;
                else if ((additionalMapMode === constants.mode.progression || isMultimap)
                        && !!this.drawMode.progression)
                    additionalMapDrawMode = constants.mode.progression;
                else
                    additionalMapDrawMode = null;

                return additionalMapDrawMode;
            },

            gateSectorsData() {
                let angle = 360.0 / this.gateLongitudes.length;

                return this.gateLongitudes.map((gateLongitude, index) => {
                    let gate = index + 1;

                    let start_angle = this.circleRotate + gateLongitude;
                    let end_angle = start_angle + angle;

                    let start = math.polarToCartesian(this.circleCenter, this.circleCenter, this.gateRadius, end_angle);
                    let end = math.polarToCartesian(this.circleCenter, this.circleCenter, this.gateRadius, start_angle);

                    return {
                        gate: gate,
                        hasPlanets: this.gateHasPlanets(gate),
                        svgData: [
                            "M", start.x, start.y,
                            "A", this.gateRadius, this.gateRadius, 0, 0, 0, end.x, end.y,
                            "L", this.circleCenter, this.circleCenter,
                            "Z"
                        ].join(" ")
                    };
                });
            },

            gateNumberCoordsList() {
                let angle = 360.0 / this.gateLongitudes.length;

                return this.gateLongitudes.map((gateLongitude, index) => {
                    let gate = index + 1;

                    let curAngle = this.circleRotate + gateLongitude + (angle * 0.5);

                    let point = math.polarToCartesian(this.circleCenter, this.circleCenter,
                            (this.gateRadius + this.zodiacRadius + this.zodiacBgThickness / 2) / 2, curAngle);

                    return {
                        gate: gate,
                        hasPlanets: this.gateHasPlanets(gate),
                        // Центрируем текст относительно его контейнера
                        x: point.x - Number(this.circleCenter),
                        y: point.y - Number(this.circleCenter)
                        // x: point.x - (this.gateNumberRectWidth / 2),
                        // y: point.y - (this.gateNumberRectHeight / 2)
                    }
                });
            },

            houseNumberCoordsList() {
                return this.houseCusps.map((houseCusp, index) => {
                    let curAngle = this.circleRotate + houseCusp.longitude;

                    let point = math.polarToCartesian(this.circleCenter, this.circleCenter,
                            this.gateRadius + this.circlePadding - this.houseWidth / 2, curAngle);

                    return {
                        houseCusp: houseCusp.houseCusp,
                        // Центрируем текст относительно его контейнера
                        x: point.x - Number(this.circleCenter),
                        y: point.y - Number(this.circleCenter)
                        // x: point.x - this.houseWidth / 2,
                        // y: point.y - this.houseWidth / 2
                    }
                });
            },

            houseLinesCoordsList() {
                return this.houseCusps.map((houseCusp, index) => {
                    let curAngle = this.circleRotate + houseCusp.longitude;

                    let point = math.polarToCartesian(this.circleCenter, this.circleCenter,
                            this.gateRadius + this.circlePadding - this.houseWidth, curAngle);

                    return {
                        bold: ['I', 'IV', 'VII', 'X'].indexOf(houseCusp.houseCusp) != -1,
                        lineData: [
                            "M", point.x, point.y,
                            "L", this.circleCenter, this.circleCenter
                        ].join(" ")
                    }
                });
            },

            hoveredHouseCuspData() {
                if (!this.hoveredHouseCusp)
                    return;

                let houseCusp = this.houseCusps.find(h => h.houseCusp == this.hoveredHouseCusp);

                return {
                    longitude: utils.formatLongitude(houseCusp.longitudeParts),
                    gate: `${houseCusp.gate}.${houseCusp.gateLine}`,
                    zodiac: houseCusp.zodiac
                }
            },

            hoveredPlanetData() {
                if (!this.hoveredPlanet.mode || !this.hoveredPlanet.planet)
                    return;

                let planetPositions;
                switch (this.hoveredPlanet.mode) {
                    case constants.mode.design:
                        planetPositions = this.designPositions;
                        break;
                    case constants.mode.transit:
                        planetPositions = this.transitPositions;
                        break;
                    case constants.mode.direction:
                        planetPositions = this.directionPositions;
                        break;
                    case constants.mode.progression:
                        planetPositions = this.progressionPositions;
                        break;
                    default:
                        planetPositions = this.natalPositions;
                }

                let planetPos = planetPositions.find(planetData => planetData.planet == this.hoveredPlanet.planet);

                return {
                    planetName: constants.planetNames.get(planetPos.planet),
                    longitude: utils.formatLongitude(planetPos.longitudeParts),
                    gate: `${planetPos.gate}.${planetPos.gateLine}`,
                    zodiac: planetPos.zodiac,
                    houseCusp: planetPos.houseCusp
                }
            },

            planetCoordsList() {
                let planetPositions = [];
                if (!!this.drawMode.natal)
                    planetPositions = planetPositions
                            .concat(this.clonePositions(this.natalPositions, constants.mode.natal));
                if (!!this.drawMode.design)
                    planetPositions = planetPositions
                            .concat(this.clonePositions(this.designPositions, constants.mode.design));

                switch (this.additionalMapDrawMode) {
                    case constants.mode.transit:
                        planetPositions = planetPositions
                                .concat(this.clonePositions(this.transitPositions, constants.mode.transit));
                        break;
                    case constants.mode.direction:
                        planetPositions = planetPositions
                                .concat(this.clonePositions(this.directionPositions, constants.mode.direction));
                        break;
                    case constants.mode.progression:
                        planetPositions = planetPositions
                                .concat(this.clonePositions(this.progressionPositions, constants.mode.progression));
                        break;
                }

                let angle = 360.0 / this.gateLongitudes.length;

                let occupiedGates = new Map();

                return planetPositions.map(planetData => {
                    let planetSignWidth = planetData.longitudeParts.retrograde ?
                            this.planetSignWidth.retrograde : this.planetSignWidth.regular;

                    let gateLongitude = this.gateLongitudes[planetData.gate - 1];
                    let curAngle = this.circleRotate + gateLongitude + angle * 0.5;

                    let planetInGateCount = occupiedGates.get(planetData.gate) || 0;

                    let point = math.polarToCartesian(this.circleCenter, this.circleCenter,
                            this.zodiacRadius - (this.zodiacBgThickness * 1.5) - (planetInGateCount * planetSignWidth * 2),
                            curAngle);

                    occupiedGates.set(planetData.gate, ++planetInGateCount);

                    return {
                        mode: planetData.mode,
                        name: planetData.planet,
                        retrograde: planetData.longitudeParts.retrograde,
                        x: point.x - (planetSignWidth / 2.0),
                        y: point.y - (planetSignWidth / 2.0),
                        signWidth: planetSignWidth
                    }
                });
            },

            planetCoordsMap() {
                let planetCoordsMap = new Map();

                Object.values(constants.mode).forEach((mode) => {
                    planetCoordsMap.set(mode, this.planetCoordsList
                            .filter(planetCoords => planetCoords.mode === mode));
                });

                return planetCoordsMap;
            },

            aspectsData() {
                let planetAspects = [];

                if (!!this.drawMode.natal) {
                    if (!!this.additionalMapDrawMode) {
                        switch (this.additionalMapDrawMode) {
                            case constants.mode.transit:
                                planetAspects = this.convertNatalAdditionalAspects(this.natalTransitAspects, constants.mode.transit);
                                break;
                            case constants.mode.direction:
                                planetAspects = this.convertNatalAdditionalAspects(this.natalDirectionAspects, constants.mode.direction);
                                break;
                            case constants.mode.progression:
                                planetAspects = this.convertNatalAdditionalAspects(this.natalProgressionAspects, constants.mode.progression);
                                break;
                        }
                    } else
                        planetAspects = this.cloneAspects(this.natalAspects, constants.mode.natal)
                } else if (!!this.additionalMapDrawMode) {
                    switch (this.additionalMapDrawMode) {
                        case constants.mode.transit:
                            planetAspects = this.cloneAspects(this.transitAspects, constants.mode.transit);
                            break;
                        case constants.mode.direction:
                            planetAspects = this.cloneAspects(this.directionAspects, constants.mode.direction);
                            break;
                        case constants.mode.progression:
                            planetAspects = this.cloneAspects(this.progressionAspects, constants.mode.progression);
                            break;
                    }
                }

                return planetAspects.map(aspectData => {
                    let planet1Coords = this.planetCoordsMap.get(aspectData.mode || aspectData.mode1)
                            .filter(c => c.name == aspectData.planet1)[0];
                    let planet2Coords = this.planetCoordsMap.get(aspectData.mode || aspectData.mode2)
                            .filter(c => c.name == aspectData.planet2)[0];

                    let halfPlanet1Width = (planet1Coords.retrograde ? this.planetSignWidth.retrograde : this.planetSignWidth.regular) / 2.0;
                    let halfPlanet2Width = (planet2Coords.retrograde ? this.planetSignWidth.retrograde : this.planetSignWidth.regular) / 2.0;
                    let avgPlanetWidth = halfPlanet1Width + halfPlanet2Width;

                    let lineData = [
                        "M", planet1Coords.x + halfPlanet1Width, planet1Coords.y + halfPlanet1Width,
                        "L", planet2Coords.x + halfPlanet2Width, planet2Coords.y + halfPlanet2Width
                    ].join(" ");

                    let typeCoords = {
                        x: (planet1Coords.x + planet2Coords.x + avgPlanetWidth - this.aspectSignWidth) / 2.0,
                        y: (planet1Coords.y + planet2Coords.y + avgPlanetWidth - this.aspectSignWidth) / 2.0
                    };

                    return {
                        mode: aspectData.mode || aspectData.mode2,
                        lineData: lineData,
                        type: aspectData.aspect,
                        typeCoords: typeCoords
                    }
                })
            },

            zodiacCircleLength() {
                return 2 * Math.PI * Number(this.zodiacRadius);
            },

            zodiacSectorDasharray() {
                let gapSize = 4;
                let dashSize = this.zodiacCircleLength / this.zodiacs.length - gapSize;
                let emptinessSize = this.zodiacCircleLength - dashSize + gapSize;
                return `${dashSize} ${emptinessSize}`;
            },

            zodiacSectorDashoffset() {
                let zodiacOffset = this.zodiacCircleLength / this.zodiacs.length;
                let rotateOffset = ((this.circleRotate > 360 ? this.circleRotate % 360.0 : this.circleRotate) / 360.0) * this.zodiacCircleLength;
                return this.zodiacs.map((zodiac, index) => {
                    return -1 * zodiacOffset * index + rotateOffset;
                });
            },

            zodiacSectorsSvgData() {
                let angle = 360.0 / this.zodiacs.length;

                return this.zodiacs.map((zodiac, index) => {
                    let start_angle = Number(this.startAngle) + angle * index + 0.5;
                    let end_angle = start_angle + angle - 0.5;

                    let start = math.polarToCartesian(this.circleCenter, this.circleCenter, this.zodiacRadius, end_angle);
                    let end = math.polarToCartesian(this.circleCenter, this.circleCenter, this.zodiacRadius, start_angle);
                    let largeArcFlag = end_angle - start_angle <= 180 ? "0" : "1";

                    let cutout_radius = this.zodiacRadius - this.zodiacBgThickness;
                    let start2 = math.polarToCartesian(this.circleCenter, this.circleCenter, cutout_radius, end_angle);
                    let end2 = math.polarToCartesian(this.circleCenter, this.circleCenter, cutout_radius, start_angle);

                    return [
                        "M", start.x, start.y,
                        "A", this.zodiacRadius, this.zodiacRadius, 0, largeArcFlag, 0, end.x, end.y,
                        "L", this.circleCenter, this.circleCenter,
                        "Z",

                        "M", start2.x, start2.y,
                        "A", cutout_radius, cutout_radius, 0, largeArcFlag, 0, end2.x, end2.y,
                        "L", this.circleCenter, this.circleCenter,
                        "Z"
                    ].join(" ");
                });
            },

            zodiacCoordsList() {
                let angle = 360.0 / this.zodiacs.length;

                return this.zodiacs.map((zodiac, index) => {
                    let curAngle = this.circleRotate + angle * (index + 0.5);

                    let point = math.polarToCartesian(this.circleCenter, this.circleCenter, this.zodiacRadius, curAngle);

                    return {
                        name: zodiac,
                        x: point.x - (this.zodiacSignWidth / 2.0),
                        y: point.y - (this.zodiacSignWidth / 2.0)
                    }
                });
            }
        },

        methods: {
            gateHasPlanets(gate) {
                let result = false;

                if (!!this.drawMode.natal)
                    result = result || (this.natalGates.includes(gate));
                if (!!this.drawMode.design)
                    result = result || (this.designGates.includes(gate));
                switch(this.additionalMapDrawMode) {
                    case constants.mode.transit:
                        result = result || (this.transitGates.includes(gate));
                        break;
                    case constants.mode.direction:
                        result = result || (this.directionGates.includes(gate));
                        break;
                    case constants.mode.progression:
                        result = result || (this.progressionGates.includes(gate));
                        break;
                }

                return result;
            },

            clonePositions(planetPositions, mode) {
                return planetPositions
                        .map(pos => {
                            let posCopy = Object.assign({}, pos);
                            return Object.assign(posCopy, {
                                mode: mode
                            })
                        });
            },

            cloneAspects(planetAspects, mode) {
                return planetAspects
                        .map(asp => {
                            let aspCopy = Object.assign({}, asp);
                            return Object.assign(aspCopy, {
                                mode: mode
                            })
                        });
            },

            convertNatalAdditionalAspects(natalAdditionalAspects, mode) {
                return natalAdditionalAspects
                        .filter(asp => !!asp.natalPlanet)
                        .map(asp => Object.assign({}, {
                            planet1: asp.natalPlanet,
                            planet2: asp.transitPlanet,
                            aspect: asp.aspect,
                            mode1: constants.mode.natal,
                            mode2: mode
                        }));
            },

            onPlanetMouseover(payload) {
                Object.assign(this.hoveredPlanet, {
                    mode: payload.mode,
                    planet: payload.planet
                });
            },

            onHouseMouseover(e, houseCusp) {
                this.hoveredHouseCusp = houseCusp;
            },

            onDataTooltipMousemove(e) {
                let dataTooltip = this.$refs.dataTooltip;

                let left = (e.clientX + 40 + dataTooltip.clientWidth > window.innerWidth) ?
                        e.clientX - dataTooltip.clientWidth - 15 :
                        e.clientX + 15;
                let top = (e.clientY + 20 + dataTooltip.clientHeight > window.innerHeight) ?
                        e.clientY - dataTooltip.clientHeight :
                        e.clientY + 15;

                if (left < 0) {
                    left = e.clientX - dataTooltip.clientWidth / 2;
                    top = e.clientY + dataTooltip.clientHeight;
                }

                this.dataTooltipCss.left = left;
                this.dataTooltipCss.top = top;
            },

            onDataTooltipMouseleave() {
                this.hoveredPlanet.planet = null;
                this.hoveredHouseCusp = null;
            }
        }
    }
</script>
